<template>
  <div
    v-if="customIsVisible && (fetchingProducts || sortedProducts.length > 0)"
    :class="[backgroundColor, bottomMargin, verticalPadding, background]"
    class="five-products"
  >
    <div v-if="fetchingProducts" class="mx-auto w-full max-w-screen-2xl px-5 md:px-12 animate-pulse">
      <InnoFiveProductsSkeleton />
    </div>
    <div v-else class="mx-auto w-full max-w-screen-2xl px-5 md:px-12">
      <VcTypography v-if="title" :tag="heading" :anchor-id="title" class="w-full mb-8">
        {{ title }}
      </VcTypography>

      <div class="flex flex-col lg:flex-row gap-4 lg:gap-8 w-full">
        <div class="w-full lg:min-h-[500px]" :class="{ 'bg-[--color-body-bg] p-4 rounded-lg': hasBackgroundColor }">
          <InnoMainItem :product="sortedProducts[0]"></InnoMainItem>
        </div>
        <div
          class="w-full lg:w-[500px] lg:flex-none flex flex-col min-gap-2 justify-evenly lg:min-h-[500px]"
          :class="{ 'bg-[--color-body-bg] p-4 rounded-lg': hasBackgroundColor }"
        >
          <InnoSubItem
            v-if="sortedProducts.length > 1"
            v-for="item in sortedProducts.slice(1)"
            :key="item.id"
            :product="item"
          ></InnoSubItem>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useProducts } from "@/shared/catalog";
import InnoMainItem from "./inno-five-products-main-item.vue";
import InnoSubItem from "./inno-five-products-sub-item.vue";
import InnoFiveProductsSkeleton from "./inno-five-products-skeleton.vue";
import { useAnalytics } from "@/core/composables/useAnalytics";

type ProductSkuType = {
  productSku: string;
};

interface IProps {
  id: string;
  heading?: string;
  title?: string;
  subtitle?: string;
  skus?: ProductSkuType[];

  background?: string;
  model: any;
  settings: any;

  // Innovadis custom: Use custom visibility setting
  customIsVisible: boolean;

  // Innovadis custom: Use custom layout settings
  backgroundColor: string;
  bottomMargin: string;
  verticalPadding: string;
}

const props = withDefaults(defineProps<IProps>(), {
  heading: "h2",
});

const { products, fetchProducts, fetchingProducts } = useProducts();
const { analytics } = useAnalytics();
const sortedProducts = ref<any[]>([]);

const hasBackgroundColor = computed<boolean>(() => {
  return props.backgroundColor !== "bg-[--color-body-bg]";
});

watchEffect(async () => {
  if (props.skus && props.skus.length > 0) {
    // Remove empty sku entries
    const productSkus = props.skus
      .map((sku) => sku.productSku)
      .filter((sku) => sku && sku.trim() !== "")
      .join(",");

    // Fetch products based on the filtered SKUs
    await fetchProducts({ filter: `"SKU":${productSkus}` });

    // Access the value of the computed products array
    const productList = products.value;

    // Reorder products based on the order of props.skus
    // The result of fetchProducts is kinda random
    const sorted = props.skus
      .map((sku) => {
        return productList.find((product: any) => product.code === sku.productSku); // Find product by code
      })
      .filter((product) => product !== undefined); // Filter out any undefined products

    // Update the sortedProducts ref with the new order
    sortedProducts.value = sorted;

    // push to google
    analytics("viewItemList", sortedProducts.value, {
      item_list_id: "five_featured_products",
      item_list_name: props.title ?? "Five featured products",
    });
  }
});
</script>
